import { type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * ModernTopBar — the command/search bar that sits above the workspace.
 * A single prominent search/command field (opens the app's existing command
 * palette via `onSearchClick`), plus a slot for primary actions and the user
 * menu. Intentionally uncluttered.
 */
export function ModernTopBar({
  title,
  subtitle,
  searchPlaceholder = 'Search & command…',
  searchHint = '⌘K',
  onSearchClick,
  actions,
  trailing,
  className,
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  searchPlaceholder?: string;
  searchHint?: string;
  onSearchClick?: () => void;
  actions?: ReactNode;
  trailing?: ReactNode;
  className?: string;
}) {
  return (
    <header
      className={cn(
        'flex h-14 flex-shrink-0 items-center gap-3 border-b border-border bg-card/80 px-4 backdrop-blur',
        className,
      )}
    >
      {(title || subtitle) && (
        <div className="hidden min-w-0 md:block">
          {title && <div className="truncate text-sm font-semibold text-foreground">{title}</div>}
          {subtitle && <div className="truncate text-[11px] text-muted-foreground">{subtitle}</div>}
        </div>
      )}

      <button
        type="button"
        onClick={onSearchClick}
        className="group flex h-9 min-w-0 flex-1 items-center gap-2 rounded-full border border-border bg-muted/50 px-3.5 text-left text-[13px] text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:max-w-md"
      >
        <Search className="h-4 w-4 flex-shrink-0" aria-hidden />
        <span className="flex-1 truncate">{searchPlaceholder}</span>
        {searchHint && (
          <kbd className="hidden flex-shrink-0 rounded border border-border bg-card px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
            {searchHint}
          </kbd>
        )}
      </button>

      <div className="flex flex-shrink-0 items-center gap-2">
        {actions}
        {trailing}
      </div>
    </header>
  );
}
